import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Row as TanTableRow, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as yup from "yup";
// Imported rather than redeclared locally. The local copy said
// `description?: string` while the response carried `description: string | null`,
// and because the fetch was typed through the local copy the typecheck could
// not see the drift. Importing the source of truth is what makes tsc catch this
// class here at all. The read itself now goes through `useFirmRiskProfiles`,
// which is typed against this same declaration and which the three holdings
// screens outside `client/src/firm` share.
import type { FirmRiskProfile } from "../../../api/src/firms/firms.service";

import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import TabContainerWithTabs from "../components/TabContainer";
import EditableTableRow, {
  EditableRowExtension,
} from "../components/Table/EditableTableRow";
import { Table, useTable } from "../components/Table/Table";
import { useAuthenticatedMutationAsync } from "../lib/api";
import { useRoles } from "../lib/auth";
import FirmNav from "./FirmNav";
import { FIRM_RISK_PROFILES_PATH, useFirmRiskProfiles } from "./lib";

type FirmRiskProfileRow = FirmRiskProfile &
  EditableRowExtension & {
    _isDeleted?: boolean;
    _isNew?: boolean;
  };

type FirmRiskProfileFields = {
  name: string;
  description: string | null;
  displayOrder: number;
};

// Exported for FirmRisk.spec.tsx. The resolver's verdict over this schema is
// what gates the save, so the null-description case is asserted directly
// against it rather than through a render.
export const schema: yup.ObjectSchema<{
  riskProfiles: FirmRiskProfileFields[];
}> = yup.object({
  riskProfiles: yup
    .array()
    .of(
      yup.object({
        name: yup
          .string()
          .required()
          .max(100, "Name must be at most 100 characters")
          .label("Name"),
        // `.optional()` relaxes `undefined` only; yup 1.x is non-nullable by
        // default. `GET /v1/firm/risk` now returns an explicit null for a risk
        // level with no description, `reset({ riskProfiles })` puts that null
        // straight into the form, and a bare `yup.string()` rejects it. Because
        // the save is gated on the resolver's verdict over the WHOLE form
        // (`isValid` below), one such row would silently disable Submit for
        // every row. Same fix as `FirmInfo.tsx:124-138`.
        description: yup.string().nullable().defined().label("Description"),
        displayOrder: yup
          .number()
          .required()
          .integer("Display order must be a whole number")
          .min(0, "Display order must be positive")
          .label("Display Order"),
      }),
    )
    .required(),
});

const FirmRisk = () => {
  const roles = useRoles();
  const {
    control,
    register,
    reset,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });
  const { insert: insertField, remove: removeField } = useFieldArray({
    name: "riskProfiles",
    control,
    keyName: "_field_id",
  });

  const [riskProfilesEdited, setRiskProfiles] = useState<FirmRiskProfile[]>([]);
  const [changedProfiles, setChangedProfiles] = useState<
    Record<number, FirmRiskProfile & { _isDeleted?: boolean; _isNew?: boolean }>
  >({});

  const handleUpdateRiskProfile = useCallback(
    (
      index: number,
      name: string,
      description: string | null,
      displayOrder: number,
    ) => {
      setRiskProfiles(
        riskProfilesEdited.map((profile, indexInner) => {
          if (index !== indexInner) {
            return profile;
          }

          const updatedProfile = {
            ...profile,
            name,
            description,
            displayOrder,
          };

          setChangedProfiles((changedProfiles) => ({
            ...changedProfiles,
            [profile.firmRiskId]: updatedProfile,
          }));

          return updatedProfile;
        }),
      );
    },
    [riskProfilesEdited],
  );

  const onConfirmRow = useCallback(
    (row: TanTableRow<FirmRiskProfileRow>) => {
      const vals = getValues(`riskProfiles.${row.index}`);
      handleUpdateRiskProfile(
        row.index,
        vals.name,
        vals.description || null,
        vals.displayOrder,
      );
    },
    [getValues, handleUpdateRiskProfile],
  );

  const onEditRow = useCallback(
    (row: TanTableRow<FirmRiskProfileRow>) => {
      setValue(`riskProfiles.${row.index}`, row.original);
    },
    [setValue],
  );

  const onDiscardRow = useCallback(
    (row: TanTableRow<FirmRiskProfileRow>) => {
      setValue(`riskProfiles.${row.index}`, row.original);
    },
    [setValue],
  );

  const onAddRow = useCallback(() => {
    const maxOrder = riskProfilesEdited.reduce(
      (max, p) => Math.max(max, p.displayOrder),
      0,
    );
    const newProfile: FirmRiskProfile & { _isNew: boolean } = {
      firmRiskId: Date.now(), // temporary ID for new rows
      firmId: 0, // will be set by backend
      name: "",
      description: null,
      displayOrder: maxOrder + 1,
      createdTime: new Date(),
      updatedTime: new Date(),
      _isNew: true,
    };

    insertField(0, newProfile, { shouldFocus: true });
    setRiskProfiles([newProfile, ...riskProfilesEdited]);
    setChangedProfiles((prev) => ({
      ...prev,
      [newProfile.firmRiskId]: newProfile,
    }));
  }, [insertField, riskProfilesEdited]);

  const onRemoveRow = useCallback(
    (row: TanTableRow<FirmRiskProfileRow>) => {
      removeField(row.index);
      setRiskProfiles(
        riskProfilesEdited.filter((_, index) => index !== row.index),
      );

      if (row.original._isNew) {
        const updated = { ...changedProfiles };
        delete updated[row.original.firmRiskId];
        setChangedProfiles(updated);
      } else {
        // Mark for deletion
        setChangedProfiles({
          ...changedProfiles,
          [row.original.firmRiskId]: { ...row.original, _isDeleted: true },
        });
      }
    },
    [changedProfiles, removeField, riskProfilesEdited],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<FirmRiskProfileRow>(),
    [],
  );

  const riskProfileErrors = errors.riskProfiles as Merge<
    FieldError,
    (Merge<FieldError, FieldErrorsImpl<FirmRiskProfileRow>> | undefined)[]
  >;

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: () => "Name",
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        meta: {
          editableComponent: (cell) => (
            <>
              <Form.Control
                type="text"
                maxLength={100}
                {...register(`riskProfiles.${cell.row.index}.name`)}
              />
              <FormFieldError
                field={riskProfileErrors?.[cell.row.index]?.name}
              />
            </>
          ),
        },
      }),
      columnHelper.accessor("description", {
        header: () => "Description",
        cell: (info) => info.getValue() || "-",
        enableColumnFilter: false,
        meta: {
          editableComponent: (cell) => (
            <>
              <Form.Control
                type="text"
                {...register(`riskProfiles.${cell.row.index}.description`)}
              />
              <FormFieldError
                field={riskProfileErrors?.[cell.row.index]?.description}
              />
            </>
          ),
        },
      }),
      columnHelper.accessor("displayOrder", {
        header: () => "Display Order",
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        meta: {
          editableComponent: (cell) => (
            <>
              <Form.Control
                type="number"
                min="0"
                step="1"
                {...register(`riskProfiles.${cell.row.index}.displayOrder`, {
                  valueAsNumber: true,
                })}
              />
              <FormFieldError
                field={riskProfileErrors?.[cell.row.index]?.displayOrder}
              />
            </>
          ),
        },
      }),
      columnHelper.accessor("updatedTime", {
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          const date = value instanceof Date ? value : new Date(value);
          return date.toLocaleDateString();
        },
        header: () => "Updated",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        size: 120,
      }),
      columnHelper.display({
        id: "add-remove",
      }),
    ],
    [columnHelper, register, riskProfileErrors],
  );

  // The route answers a PAGE, not the whole collection (APP-1563), so one
  // response is never the answer on its own. `useFirmRiskProfiles` walks it —
  // shared with the three screens outside `client/src/firm` that read the same
  // collection. Nothing in `tsc` can catch getting this wrong: the element type
  // of a page and of the whole array are the same. `FirmRisk.pagination.spec.tsx`
  // is what holds it.
  const {
    riskProfiles,
    hasEveryRiskProfile,
    isPendingFirstPage: isPendingRiskProfiles,
    isError: isErrorRiskProfiles,
    reload: reloadRiskProfiles,
  } = useFirmRiskProfiles();

  useEffect(() => {
    // Copied into the editable state only once the walk HOLDS every risk level.
    // A copy per page would hand the screen a partial collection to edit, and
    // `reset` would then throw away an edit made to a row the walk had already
    // delivered every time the next page landed.
    if (hasEveryRiskProfile) {
      reset({ riskProfiles });
      setRiskProfiles(riskProfiles);
    }
  }, [hasEveryRiskProfile, riskProfiles, reset]);

  const { table } = useTable({
    columns: columns,
    data: riskProfilesEdited,
    getRowId: (row) => row.firmRiskId.toString(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
    autoResetAll: false,
  });

  const createMutation = useAuthenticatedMutationAsync<{
    data: FirmRiskProfile;
  }>("/v1/firm/risk", async (data: Omit<FirmRiskProfileFields, "_isNew">) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const { getAccessTokenSilently } = useAuth0();

  const updateProfile = useCallback(
    async (profile: FirmRiskProfile) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/v1/firm/risk/${profile.firmRiskId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: profile.name,
            description: profile.description,
            displayOrder: profile.displayOrder,
          }),
        },
      );
      if (!response.ok) throw new Error("Failed to update risk level");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  const deleteProfile = useCallback(
    async (id: number) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/v1/firm/risk/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) throw new Error("Failed to delete risk level");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  const handleDiscardChanges = useCallback(() => {
    setRiskProfiles(riskProfiles);
    reset({ riskProfiles });
    setChangedProfiles({});
  }, [reset, riskProfiles]);

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async () => {
      if (isValid) {
        try {
          // Process each changed profile
          for (const profile of Object.values(changedProfiles)) {
            if (profile._isDeleted) {
              await deleteProfile(profile.firmRiskId);
            } else if (profile._isNew) {
              await createMutation({
                name: profile.name,
                description: profile.description,
                displayOrder: profile.displayOrder,
              });
            } else {
              await updateProfile(profile);
            }
          }

          // The read is a walk over pages now (APP-1563), so its cache holds
          // one entry per page keyed `[path, offset]`. Invalidating the PATH
          // matches every one of them; the old key named the unversioned path
          // this screen no longer reads and would match nothing.
          //
          // The collected pages are dropped as well, and that half is not
          // optional: the walk stops asking once it holds the whole
          // collection, so an invalidation on its own would mark entries stale
          // that nothing is subscribed to any more and refetch nothing at all.
          queryClient.invalidateQueries({
            queryKey: [FIRM_RISK_PROFILES_PATH],
          });
          reloadRiskProfiles();
          setChangedProfiles({});
          notificationContext.pushNotification({
            id: "firm-risk",
            header: "Risk Updated",
            body: "Risk levels updated successfully",
            variant: "success",
          });
        } catch (err) {
          console.error("Failed to save risk levels", err);
          notificationContext.pushNotification({
            id: "firm-risk",
            header: "Failed to Save Risk",
            body: "Risk levels not saved",
            variant: "danger",
          });
        }
      }
    },
  });

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      {/* Only while the FIRST page is outstanding. Every further page turns
          the hook pending again under a new key, and unmounting the table for
          each of them would flash the screen through Loading once per page. */}
      {isPendingRiskProfiles ? (
        <Loading />
      ) : isErrorRiskProfiles ? (
        <Alert variant="danger">Failed to retrieve risk levels</Alert>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4>Risk</h4>
              <p className="text-muted">
                Configure risk levels for your firm (e.g., Growth, Defensive).
                Each level defines how asset classes are allocated between
                growth and defensive strategies.
              </p>
            </Col>
          </Row>
          {!roles.includes("Admin") ||
          Object.keys(changedProfiles).length <= 0 ? null : (
            <Row className="mb-2">
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="success"
                  onClick={() => onSubmit.mutateAsync()}
                  disabled={onSubmit.isPending}
                >
                  Submit{" "}
                  {onSubmit.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    ""
                  )}
                </Button>
              </Col>
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="danger"
                  onClick={handleDiscardChanges}
                  disabled={onSubmit.isPending}
                >
                  Discard
                </Button>
              </Col>
            </Row>
          )}
          <Row>
            <Col
              style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}
            >
              <FormError
                hasError={!isValid}
                message="Please fix the errors above."
              />
              <Table
                table={table}
                RowComp={roles.includes("Admin") ? EditableTableRow : undefined}
                onSave={onConfirmRow}
                onEdit={onEditRow}
                onDiscard={onDiscardRow}
                enableAddRemove
                onAdd={onAddRow}
                onRemove={onRemoveRow}
              />
            </Col>
          </Row>
        </>
      )}
    </TabContainerWithTabs>
  );
};

export default FirmRisk;
